import React, { useCallback, useContext, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { capitalize } from "lodash";
import { useModal } from "react-modal-hook";
import {
  ARCGIS_EXPORT_FREQUENCY,
  SAVED_VIEW_EXPORT_DESTINATION,
} from "common/constants";
import { generateTableLabel } from "common/utils/queryBuilder";
import {
  useDeleteArcGisExportConfigurationMutation,
  useGetArcGisExportConfigurationQuery,
  useGetScriptJobsQuery,
  useManualExportToArcGisMutation,
  useUpsertArcGisExportConfigurationMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import ArcGISLogo from "../../../../images/integrations/arcgis-logo.png";
import { spacing } from "../../../../stitches.config";
import { AuthContext } from "../../../Authorization/AuthContext";
import { Account } from "../../../Authorization/types";
import { Box, FlexColumn, FlexRow } from "../../../Common/__styles__/Layout";
import { CommonModal } from "../../../Common/CommonModal";
import { Icons } from "../../../Common/Icons";
import Modal from "../../../Common/Modal";
import { useManualPaginationConfig } from "../../../Common/Tables/hooks";
import Table from "../../../Common/Tables/Table";
import { Body, Title } from "../../../Common/Typography";
import { DropdownMenu, Select, Switch } from "../../../Inputs";
import RecentJobsTable from "../RecentJobsTable";

type SavedViewTableProps = Pick<
  NonNullable<Account["savedViews"][number]>,
  "id" | "name" | "query"
>;

const ConfirmDisableSavedViewModal = ({
  savedViewName,
  savedViewTable,
  onCancel,
  onConfirm,
}: {
  savedViewName: string;
  savedViewTable: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  return (
    <CommonModal
      title="Send to ArcGIS"
      subtitle={`${savedViewName} - ${savedViewTable}`}
      onCancel={onCancel}
      onSubmit={onConfirm}
      primaryButtonText="Disable"
      variant="destructive"
      content={
        <Body size="large" type="regular">
          Disabling this layer will stop sharing its data with ArcGIS. On the
          next export, this data will no longer be available in ArcGIS.
        </Body>
      }
    />
  );
};

const SwitchForSavedView = ({
  currentSavedViewIds,
  savedViewId,
  savedViewName,
  savedViewTable,
  onSavedViewToggle,
}: {
  currentSavedViewIds: string[];
  savedViewId: string;
  savedViewName: string;
  savedViewTable: string;
  onSavedViewToggle: (savedViewIds: string[]) => void;
}) => {
  const [showConfirmDisableSavedViewModal, hideConfirmDisableSavedViewModal] =
    useModal(
      () => (
        <Modal>
          <ConfirmDisableSavedViewModal
            savedViewName={savedViewName}
            savedViewTable={savedViewTable}
            onCancel={hideConfirmDisableSavedViewModal}
            onConfirm={() =>
              onSavedViewToggle(
                currentSavedViewIds.filter(id => id !== savedViewId),
              )
            }
          />
        </Modal>
      ),
      [
        savedViewName,
        savedViewTable,
        onSavedViewToggle,
        currentSavedViewIds,
        savedViewId,
      ],
    );

  return (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <div style={{ width: "fit-content" }}>
        <Switch
          name={savedViewId}
          value={currentSavedViewIds.includes(savedViewId)}
          size="small"
          onChange={sendToArcGIS => {
            if (sendToArcGIS) {
              onSavedViewToggle([...currentSavedViewIds, savedViewId]);
            } else {
              showConfirmDisableSavedViewModal();
            }
          }}
        />
      </div>
    </div>
  );
};

const SavedViewTable = ({
  account,
  currentSavedViewIds,
  onSavedViewToggle,
  loading,
}: {
  account: Account;
  currentSavedViewIds: string[];
  onSavedViewToggle: (savedViewIds: string[]) => void;
  loading: boolean;
}) => {
  const columns: Array<ColumnDef<SavedViewTableProps>> = [
    {
      id: "name",
      header: "Saved view",
      accessorKey: "name",
      enableSorting: false,
      minSize: 300,
    },
    {
      id: "table",
      header: "Table",
      enableSorting: false,
      minSize: 300,
      cell: ({ row }) => {
        return (
          <Body size="default" type="regular">
            {generateTableLabel(
              row.original.query.source,
              account.customObjectGroups,
            )}
          </Body>
        );
      },
    },
    {
      id: "sendToArcGIS",
      header: () => <div style={{ textAlign: "right" }}>Send to ArcGIS</div>,
      enableSorting: false,
      cell: ({ row }) => {
        return (
          <SwitchForSavedView
            currentSavedViewIds={currentSavedViewIds}
            savedViewId={row.original.id}
            savedViewName={row.original.name}
            savedViewTable={generateTableLabel(
              row.original.query.source,
              account.customObjectGroups,
            )}
            onSavedViewToggle={onSavedViewToggle}
          />
        );
      },
    },
  ];

  return (
    <Table<SavedViewTableProps>
      columns={columns}
      currentData={account.savedViews}
      initialState={{
        pagination: {
          pageIndex: 0,
          pageSize: 10,
        },
      }}
      loadingDetails={{
        loading,
        loadingText: "Loading Saved Views...",
        noDataText: "No Saved Views Found",
      }}
      tableStyleDetails={{
        hasRowActions: false,
        isCompact: true,
        hasFlushedSides: true,
      }}
    />
  );
};

type ArcGISExportSettingsModalProps = {
  currentFrequency: ARCGIS_EXPORT_FREQUENCY;
  closeModal: () => void;
  onUpdate: (frequency: ARCGIS_EXPORT_FREQUENCY) => void;
};

export const ArcGISExportSettingsModal = ({
  currentFrequency,
  closeModal,
  onUpdate,
}: ArcGISExportSettingsModalProps) => {
  const [frequency, setFrequency] = useState(currentFrequency);

  const handleSubmit = () => {
    onUpdate(frequency);
    closeModal();
  };

  return (
    <CommonModal
      height="short"
      dataTestId="arcgis-export-settings-modal"
      title={"ArcGIS export settings"}
      onSubmit={handleSubmit}
      primaryButtonText="Save"
      autoFocusCancelButton={true}
      variant={"primary"}
      onCancel={closeModal}
      size="small"
      content={
        <Select
          name="formYear"
          value={frequency}
          onChange={value => setFrequency(value ?? currentFrequency)}
          label="Frequency of export"
          required
          options={Object.values(ARCGIS_EXPORT_FREQUENCY).map(frequency => {
            return {
              value: frequency,
              label: capitalize(FREQUENCY_MAP[frequency]),
            };
          })}
        />
      }
    />
  );
};

export const FREQUENCY_MAP: Record<ARCGIS_EXPORT_FREQUENCY, string> = {
  [ARCGIS_EXPORT_FREQUENCY.WEEKLY]: "weekly",
  [ARCGIS_EXPORT_FREQUENCY.DAILY]: "daily",
  [ARCGIS_EXPORT_FREQUENCY.HOURLY]: "hourly",
  [ARCGIS_EXPORT_FREQUENCY.HALF_HOURLY]: "every 30 minutes",
};

const ArcGISExports = () => {
  const { account } = useContext(AuthContext);

  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const {
    data: exportConfigurationData,
    refetch: refetchExportConfiguration,
    loading: exportConfigurationLoading,
  } = useGetArcGisExportConfigurationQuery({
    variables: { accountId: account?.id! },
    skip: !account?.id,
  });

  const manualPaginationConfig = useManualPaginationConfig({ pageSize: 10 });

  const {
    data: recentExportTasksData,
    loading: recentExportTasksLoading,
    refetch: refetchRecentExportTasks,
  } = useGetScriptJobsQuery({
    fetchPolicy: "no-cache",
    pollInterval: 10000,
    variables: {
      accountId: account?.id!,
      scriptName: "system/export-saved-views",
      argumentsFilter: {
        exportDestination: SAVED_VIEW_EXPORT_DESTINATION.ARCGIS,
      },
      page: manualPaginationConfig.pagination.pageIndex + 1,
      pageSize: manualPaginationConfig.pagination.pageSize,
    },
    skip: !account?.id,
  });

  const recentExportTasks =
    recentExportTasksData?.account?.scriptJobs.data.map(job => ({
      ...job,
      error: job.error?.message ?? undefined,
      name: "Saved view export",
      icon: Icons.ARC_GIS,
      stoppedAt: job.stoppedAt ?? undefined,
    })) ?? [];

  const details = useMemo(
    () => exportConfigurationData?.account?.arcGISExportConfiguration?.details,
    [exportConfigurationData],
  );

  const { savedViewIds: currentSavedViewIds, frequency: currentFrequency } =
    details ?? {
      savedViewIds: [],
      frequency: null,
    };

  const [upsertArcGISExportConfiguration] =
    useUpsertArcGisExportConfigurationMutation({
      onCompleted: () => {
        addSuccessToast("ArcGIS export configuration saved");
      },
      onError: () => {
        addErrorToast("Error saving ArcGIS export configuration");
      },
    });

  const [manualExportToArcGIS] = useManualExportToArcGisMutation({
    onCompleted: async () => {
      await refetchRecentExportTasks();
      addSuccessToast("ArcGIS export started");
    },
    onError: () => {
      addErrorToast("Error starting ArcGIS export");
    },
  });

  const [deleteArcGISExportConfiguration] =
    useDeleteArcGisExportConfigurationMutation({
      onCompleted: () => {
        addSuccessToast("ArcGIS exports disabled");
      },
      onError: () => {
        addErrorToast("Error disabling ArcGIS exports");
      },
    });

  const updateArcGISExportConfiguration = useCallback(
    async (frequency: ARCGIS_EXPORT_FREQUENCY, savedViewIds: string[]) => {
      if (frequency === null || savedViewIds.length === 0) {
        await deleteArcGISExportConfiguration();
      } else {
        await upsertArcGISExportConfiguration({
          variables: {
            details: {
              frequency,
              savedViewIds,
            },
          },
        });
      }
      await refetchExportConfiguration();
    },
    [
      upsertArcGISExportConfiguration,
      refetchExportConfiguration,
      deleteArcGISExportConfiguration,
    ],
  );

  const [showSettingsModal, hideSettingsModal] = useModal(() => {
    if (!currentFrequency) {
      return null;
    }
    return (
      <Modal onRequestClose={hideSettingsModal}>
        <ArcGISExportSettingsModal
          currentFrequency={currentFrequency}
          closeModal={hideSettingsModal}
          onUpdate={frequency =>
            updateArcGISExportConfiguration(frequency, currentSavedViewIds)
          }
        />
      </Modal>
    );
  }, [currentFrequency, currentSavedViewIds, updateArcGISExportConfiguration]);

  const subtitle = useMemo(() => {
    return currentFrequency ?
        `Updates ${FREQUENCY_MAP[currentFrequency]}`
      : "Select at one least saved view to enable exports";
  }, [currentFrequency]);

  const dropdownActions = useMemo(
    () => [
      {
        label: "Manual Export",
        onClick: () => manualExportToArcGIS(),
        disabled: !details,
      },
      {
        label: "Settings",
        onClick: () => {
          showSettingsModal();
        },
        disabled: !details,
      },
    ],
    [manualExportToArcGIS, showSettingsModal, details],
  );

  if (!account) {
    return null;
  }
  return (
    <Box>
      <FlexColumn style={{ padding: spacing.l.value, gap: spacing.l.value }}>
        <FlexRow style={{ justifyContent: "space-between" }}>
          <FlexRow style={{ gap: spacing.m.value, alignItems: "center" }}>
            <img src={ArcGISLogo} alt="ArcGIS" width={40} height={40} />
            <FlexColumn>
              <Title size="small" type="semiBold">
                ArcGIS
              </Title>
              <Body size="default" type="regular" color="contentSecondary">
                {subtitle}
              </Body>
            </FlexColumn>
          </FlexRow>
          <DropdownMenu actions={dropdownActions} />
        </FlexRow>
        <div>
          <Body size="default" type="regular" as="span">
            Proper configuration and sharing of ArcGIS Saved View Exports
            requires additional setup within Forerunner's ArcGIS Online account.
            Please refer to our{" "}
          </Body>
          <Body size="default" type="link" as="span">
            <a
              href="https://www.notion.so/withforerunner/SOP-Sharing-Customer-Data-via-An-ArcGIS-REST-Service-25c19d9e82cc807bab22cb0f15febcd7"
              target="_blank"
              rel="noopener noreferrer"
            >
              SOP documentation
            </a>
          </Body>
          <Body size="default" type="regular" as="span">
            {" "}
            for step-by-step instructions. To check current export status, visit
            our{" "}
          </Body>
          <Body size="default" type="link" as="span">
            <a
              href="https://withforerunner.maps.arcgis.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              ArcGIS Online instance
            </a>
          </Body>
          <Body as="span" size="default" type="regular">
            .
          </Body>
        </div>
        <SavedViewTable
          account={account}
          currentSavedViewIds={currentSavedViewIds}
          onSavedViewToggle={savedViewIds =>
            updateArcGISExportConfiguration(
              currentFrequency ?? ARCGIS_EXPORT_FREQUENCY.WEEKLY,
              savedViewIds,
            )
          }
          loading={exportConfigurationLoading}
        />
        <div>
          <Body size="large" type="emphasis">
            Recent exports
          </Body>
          <RecentJobsTable
            recentJobs={recentExportTasks}
            loading={recentExportTasksLoading}
            manualPaginationConfig={{
              ...manualPaginationConfig,
              pageCount:
                recentExportTasksData?.account?.scriptJobs.pageInfo
                  .totalPages ?? 1,
            }}
            loadingText="Loading recent exports"
            noDataText="No recent exports found"
          />
        </div>
      </FlexColumn>
    </Box>
  );
};

export default ArcGISExports;
